import { useEffect, useState } from "react";
import axios from "axios";
import { Bot, FileSignature, Loader2, Save, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { streamStaffAi } from "@/utils/aiStream";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ConsultationAiPanel = ({ consultation, onSaved }) => {
  const [notes, setNotes] = useState(consultation.staff_notes || "");
  const [output, setOutput] = useState("");
  const [active, setActive] = useState("");

  useEffect(() => {
    setNotes(consultation.staff_notes || "");
    setOutput("");
  }, [consultation.id, consultation.staff_notes]);

  const saveNotes = async () => {
    try {
      await axios.patch(`${API}/staff/consultations/${consultation.id}/notes`, { notes });
      toast.success("Staff notes saved");
      onSaved();
    } catch (error) {
      toast.error("Notes could not be saved");
    }
  };

  const runFeature = async (feature, path) => {
    if (feature === "notes" && !consultation.staff_notes) {
      toast.error("Save staff notes before summarizing them");
      return;
    }
    setActive(feature);
    setOutput("");
    try {
      await streamStaffAi(path, { consultation_id: consultation.id }, setOutput);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActive("");
    }
  };

  return (
    <section className="mt-6 rounded-lg border border-brand-navy/10 bg-brand-cream/55 p-5" data-testid={`ai-panel-${consultation.id}`}>
      <div className="flex items-center gap-3">
        <Bot className="h-5 w-5 text-brand-copper" />
        <h3 className="font-serif text-xl font-bold">AI Staff Assistant</h3>
      </div>
      <Textarea
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
        placeholder="Add private consultation notes, follow-up details, or case observations."
        data-testid={`staff-notes-${consultation.id}`}
        className="mt-4 min-h-28 border-brand-navy/15 bg-white"
      />
      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Button variant="outline" onClick={saveNotes} data-testid={`save-notes-${consultation.id}`} className="rounded-full">
          <Save className="h-4 w-4" /> Save Notes
        </Button>
        <Button variant="outline" disabled={active !== ""} onClick={() => runFeature("notes", `/staff/ai/notes-summary`)} data-testid={`summarize-notes-${consultation.id}`} className="rounded-full">
          {active === "notes" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Notes Summary
        </Button>
        <Button variant="outline" disabled={active !== ""} onClick={() => runFeature("summary", `/staff/ai/inquiry-summary`)} data-testid={`ai-summary-${consultation.id}`} className="rounded-full">
          {active === "summary" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI Summary
        </Button>
        <Button variant="outline" disabled={active !== ""} onClick={() => runFeature("reply", `/staff/ai/draft-reply`)} data-testid={`draft-reply-${consultation.id}`} className="rounded-full">
          {active === "reply" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />} Draft Reply
        </Button>
      </div>
      {output && (
        <div className="mt-4 rounded-lg bg-white p-4 text-sm leading-7 text-brand-navy/78 whitespace-pre-wrap" data-testid={`ai-output-${consultation.id}`}>
          {output}
        </div>
      )}
      <p className="mt-4 text-xs font-semibold leading-5 text-brand-navy/52">
        AI output is staff assistance only. Egaal provides document preparation and administrative support, not legal advice or representation.
      </p>
    </section>
  );
};
